// Single row of the values table (attribute-values-table.tsx) and of the
// cross-type search results (attribute-search-results.tsx). Deliberately its
// own component — not inlined in either table — so both callers can share it:
// the management table wraps it in `SortableAttributeValueRow` (dnd-kit) for
// drag-reorder, while search results render it plain (reordering only makes
// sense within a single type, so it's simply never wrapped there — that's
// also how drag-reorder ends up "disabled while searching", per the plan: the
// per-type table (the only place dragging is wired) isn't even mounted then).

import { useSortable } from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { AttributeUsage, AttributeValueAdmin, usageFor, useUpdateAttributeValueApi } from 'apis/product-attributes';
import type { DealCategoryTreeNode } from 'apis/supplier-prices';
import { DragHandle } from 'components/base-table/components';
import { findCategoryPath } from 'components/category-picker/category-tree-helpers';
import {
  Badge,
  HStack,
  Icon,
  IconButtonWithTooltip,
  Switch,
  Td,
  Text,
  Tooltip,
  Tr,
  useToast,
  Wrap,
  WrapItem,
} from 'design-system';
import { forwardRef } from 'react';
import { FiEdit, FiTrash2 } from 'react-icons/fi';

// state/origin are column-backed (master_products.state / .origin), not EAV —
// the BE always returns their values unfiltered and REJECTS (400) any
// category-binding write for them, so the per-row bindings control is
// disabled here rather than left to fail server-side. Same rule applies
// wherever this row renders, including the cross-type search results view.
const COLUMN_BACKED_TYPE_KEYS = new Set(['state', 'origin']);

interface Props {
  value: AttributeValueAdmin;
  typeKey: string;
  usage?: AttributeUsage;
  categoryTree: DealCategoryTreeNode[];
  /** Adds a leading "Type" cell — cross-type search results only. */
  typeLabel?: string;
  /** Leading drag-handle cell, supplied by `SortableAttributeValueRow`. */
  dragHandleSlot?: React.ReactNode;
  style?: React.CSSProperties;
  onOpenBindings: () => void;
  onEdit: () => void;
  onDelete: () => void;
}

export const AttributeValueRow = forwardRef<HTMLTableRowElement, Props>(
  (
    { value, typeKey, usage, categoryTree, typeLabel, dragHandleSlot, style, onOpenBindings, onEdit, onDelete },
    ref
  ) => {
    const toast = useToast();

    const updateApi = useUpdateAttributeValueApi({
      onError: (error: any) => {
        // hooks toast server messages; this is the network-error fallback
        if (!error?.response?.data?.message) toast({ title: 'Failed to update attribute value', status: 'error' });
      },
    });

    const toggleActive = (isActive: boolean) => updateApi.mutate({ id: value.id, data: { isActive } });

    const usageCount = usageFor(usage, typeKey, value.value);
    // Field absent until the paired BE deploys (UMS #2206) — degrade to the
    // "All (inherits type)" chip below, never crash on missing data.
    const boundCategoryIds = value.boundCategoryIds ?? [];
    const isColumnBacked = COLUMN_BACKED_TYPE_KEYS.has(typeKey);
    const stop = (e: React.MouseEvent) => e.stopPropagation();

    return (
      <Tr ref={ref} style={style}>
        {dragHandleSlot && <Td w='1'>{dragHandleSlot}</Td>}
        {typeLabel !== undefined && (
          <Td fontSize='xs' color='gray.600' fontWeight='medium' whiteSpace='nowrap'>
            {typeLabel}
          </Td>
        )}
        <Td>{value.label}</Td>
        <Td fontFamily='mono' fontSize='xs' color='gray.500'>
          {value.value}
        </Td>
        <Td fontSize='xs' color='gray.500'>
          {(value.synonyms ?? []).join(', ')}
        </Td>
        <Td onClick={isColumnBacked ? undefined : stop}>
          {isColumnBacked ? (
            <Tooltip label='Backs a product column — available everywhere'>
              <Badge colorScheme='gray' cursor='default'>
                All
              </Badge>
            </Tooltip>
          ) : boundCategoryIds.length === 0 ? (
            <Badge colorScheme='gray' cursor='pointer' onClick={onOpenBindings}>
              All (inherits type)
            </Badge>
          ) : (
            <Wrap spacing='1' cursor='pointer' onClick={onOpenBindings}>
              {boundCategoryIds.map((id) => {
                const path = findCategoryPath(categoryTree, id);
                const leaf = path.length ? path[path.length - 1].title || `#${id}` : `#${id}`;
                const breadcrumb = path.length
                  ? path.map((p) => p.title || `Untitled (${p.id})`).join(' › ')
                  : `Category #${id}`;
                return (
                  <WrapItem key={id}>
                    <Tooltip label={breadcrumb}>
                      <Badge colorScheme='red' variant='subtle'>
                        {leaf}
                      </Badge>
                    </Tooltip>
                  </WrapItem>
                );
              })}
            </Wrap>
          )}
        </Td>
        <Td>
          {usageCount > 0 ? (
            <Tooltip
              label={`used by ${usageCount} product${usageCount === 1 ? '' : 's'} — deactivate instead of delete`}>
              <Badge colorScheme='orange' variant='subtle'>
                {usageCount}
              </Badge>
            </Tooltip>
          ) : (
            <Text fontSize='xs' color='gray.400'>
              0
            </Text>
          )}
        </Td>
        <Td onClick={stop}>
          <Switch isChecked={value.isActive} onChange={(e) => toggleActive(e.target.checked)} colorScheme='green' />
        </Td>
        <Td>
          <HStack spacing='0'>
            <IconButtonWithTooltip
              size='sm'
              label='Edit'
              aria-label='edit-value-btn'
              Icon={<Icon as={FiEdit} />}
              variant='unstyled'
              onClick={onEdit}
            />
            <IconButtonWithTooltip
              size='sm'
              label='Delete'
              aria-label='delete-value-btn'
              Icon={<Icon as={FiTrash2} />}
              variant='unstyled'
              onClick={onDelete}
            />
          </HStack>
        </Td>
      </Tr>
    );
  }
);
AttributeValueRow.displayName = 'AttributeValueRow';

type SortableProps = Omit<Props, 'dragHandleSlot' | 'style'>;

/**
 * Drag-reorder wrapper used only by the per-type management table
 * (attribute-values-table.tsx), inside its DndContext/SortableContext. Search
 * results and the category lens preview never mount this — the underlying
 * plain `AttributeValueRow` is used there instead, which is how dragging ends
 * up unavailable in those modes without an extra disabled flag.
 */
export const SortableAttributeValueRow = (props: SortableProps) => {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({
    id: props.value.id,
  });

  const style: React.CSSProperties = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.6 : 1,
  };

  return (
    <AttributeValueRow
      {...props}
      ref={setNodeRef}
      style={style}
      dragHandleSlot={<DragHandle isDragging={isDragging} {...attributes} {...listeners} />}
    />
  );
};
